/**
 * After a work-order invoice is created, offer the documents that actually
 * exist for that invoice (receipt, invoice copy, delivery report) so the
 * counter can open them in one click (#2127).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/hooks/useNotify';
import { openInvoicePdf, openReceiptPdf } from '@/hooks/useInvoices';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { translateBillingError } from '@/lib/billingErrors';
import { getCustomerDisplayName } from '../../utils/customer';
import { generateDeliveryReport } from './DeliveryReportView';

/** Same `vs-` prefix as work-order detail card order (`vs-workorder-detail-card-order`). */
export const POST_INVOICE_DOCUMENTS_STORAGE_KEY = 'vs-post-invoice-documents';

export interface PostInvoiceDocumentSelection {
  receipt: boolean;
  invoiceCopy: boolean;
  deliveryReport: boolean;
}

const DEFAULT_SELECTION: PostInvoiceDocumentSelection = {
  receipt: true,
  invoiceCopy: true,
  deliveryReport: true,
};

export interface PostInvoiceDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId?: string;
  receiptId?: string;
  workOrder?: WorkOrderDetail;
}

function loadSelection(): PostInvoiceDocumentSelection {
  try {
    const raw = localStorage.getItem(POST_INVOICE_DOCUMENTS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SELECTION };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_SELECTION };
    const obj = parsed as Record<string, unknown>;
    return {
      receipt: typeof obj.receipt === 'boolean' ? obj.receipt : DEFAULT_SELECTION.receipt,
      invoiceCopy: typeof obj.invoiceCopy === 'boolean' ? obj.invoiceCopy : DEFAULT_SELECTION.invoiceCopy,
      deliveryReport: typeof obj.deliveryReport === 'boolean'
        ? obj.deliveryReport
        : DEFAULT_SELECTION.deliveryReport,
    };
  } catch {
    return { ...DEFAULT_SELECTION };
  }
}

function saveSelection(selection: PostInvoiceDocumentSelection): void {
  try {
    localStorage.setItem(POST_INVOICE_DOCUMENTS_STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Ignore quota / private-mode failures, matching other portal preferences.
  }
}

function toDeliveryReportData(workOrder: WorkOrderDetail) {
  const mechanicName = workOrder.mechanic
    ? [workOrder.mechanic.firstName, workOrder.mechanic.lastName].filter(Boolean).join(' ')
    : '';
  const vehicle = workOrder.vehicle;
  return {
    workOrder: {
      id: workOrder.id,
      workOrderNumber: workOrder.workOrderNumber ?? `WO-${workOrder.id.slice(0, 8).toUpperCase()}`,
      vehicleRegistration: vehicle?.registrationNumber ?? '',
      customerName: workOrder.customer ? getCustomerDisplayName(workOrder.customer) : '',
      services: workOrder.services.map((service) => ({ name: service.name, status: service.status })),
      ...(vehicle?.make ? { vehicleMake: vehicle.make } : {}),
      ...(vehicle?.model ? { vehicleModel: vehicle.model } : {}),
      ...(vehicle?.year != null ? { vehicleYear: vehicle.year } : {}),
      ...(vehicle?.mileage != null ? { mileage: vehicle.mileage } : {}),
      ...(workOrder.completedAt ? { completedAt: workOrder.completedAt } : {}),
    },
    ...(mechanicName ? { mechanicName } : {}),
  };
}

export function PostInvoiceDocumentsDialog({
  open,
  onClose,
  invoiceId,
  receiptId,
  workOrder,
}: PostInvoiceDocumentsDialogProps) {
  const { t } = useTranslation('invoices');
  const notify = useNotify();
  const [selection, setSelection] = useState<PostInvoiceDocumentSelection>(loadSelection);

  const showReceipt = Boolean(receiptId);
  const showInvoiceCopy = Boolean(invoiceId);
  const showDeliveryReport = workOrder !== undefined;

  useEffect(() => {
    if (!open) return;
    setSelection(loadSelection());
  }, [open]);

  const updateSelection = (key: keyof PostInvoiceDocumentSelection, checked: boolean) => {
    setSelection((current) => {
      const next = { ...current, [key]: checked };
      saveSelection(next);
      return next;
    });
  };

  const receiptSelected = showReceipt && selection.receipt;
  const invoiceCopySelected = showInvoiceCopy && selection.invoiceCopy;
  const deliveryReportSelected = showDeliveryReport && selection.deliveryReport;
  const hasSelection = receiptSelected || invoiceCopySelected || deliveryReportSelected;

  const handleOpenSelected = () => {
    if (!hasSelection) return;
    saveSelection(selection);
    // Browser popup blocker: every window.open must start in this click handler,
    // synchronously, without awaiting a previous opener's promise (#2127).
    if (receiptSelected && receiptId) {
      void openReceiptPdf(receiptId).catch((err: unknown) => {
        notify.error(t('payments.openReceiptFailed'), translateBillingError(err, t));
      });
    }
    if (invoiceCopySelected && invoiceId) {
      void openInvoicePdf(invoiceId).catch((err: unknown) => {
        notify.error(t('actions.previewInvoiceFailed'), translateBillingError(err, t));
      });
    }
    if (deliveryReportSelected && workOrder) {
      generateDeliveryReport(toDeliveryReportData(workOrder));
    }
    onClose();
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={t('postInvoiceDocuments.title')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('postInvoiceDocuments.title')}</DialogTitle>
      <DialogContent data-testid="post-invoice-documents-dialog">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('postInvoiceDocuments.description')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {showReceipt && (
            <FormControlLabel
              data-testid="post-invoice-document-receipt"
              control={
                <Checkbox
                  checked={selection.receipt}
                  onChange={(event) => updateSelection('receipt', event.target.checked)}
                />
              }
              label={t('postInvoiceDocuments.receipt')}
            />
          )}
          {showInvoiceCopy && (
            <FormControlLabel
              data-testid="post-invoice-document-invoice"
              control={
                <Checkbox
                  checked={selection.invoiceCopy}
                  onChange={(event) => updateSelection('invoiceCopy', event.target.checked)}
                />
              }
              label={t('postInvoiceDocuments.invoiceCopy')}
            />
          )}
          {showDeliveryReport && (
            <FormControlLabel
              data-testid="post-invoice-document-delivery-report"
              control={
                <Checkbox
                  checked={selection.deliveryReport}
                  onChange={(event) => updateSelection('deliveryReport', event.target.checked)}
                />
              }
              label={t('postInvoiceDocuments.deliveryReport')}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="post-invoice-skip">
          {t('postInvoiceDocuments.skip')}
        </Button>
        <Button
          variant="contained"
          onClick={handleOpenSelected}
          disabled={!hasSelection}
          data-testid="post-invoice-open-selected"
        >
          {t('postInvoiceDocuments.openSelected')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
